import type { EmailTemplateBlock } from '@marketing-hub/shared';
import { Button } from '@/components/ui/Button';
import { Field, TextArea, TextInput } from '@/components/ui/Field';
import {
  insertBlockAfter,
  moveBlock,
  removeBlockAt,
  updateBlockAt,
} from './emailTemplateBlockTypes';
import styles from './EmailTemplateEditor.module.css';

const BLOCK_LABELS: Record<EmailTemplateBlock['type'], string> = {
  heading: 'Kop',
  paragraph: 'Alinea',
  divider: 'Scheidingslijn',
  cta: 'Knop / link',
};

export type BlockFocusField = 'text' | 'label' | 'url_template';

type Props = {
  blocks: EmailTemplateBlock[];
  onChange: (blocks: EmailTemplateBlock[]) => void;
  disabled?: boolean;
  improvingBlockIndex?: number | null;
  onImproveBlock?: (index: number) => void;
  onFocusField?: (index: number, field: BlockFocusField) => void;
};

export function EmailTemplateBlockEditor({
  blocks,
  onChange,
  disabled,
  improvingBlockIndex,
  onImproveBlock,
  onFocusField,
}: Props) {
  return (
    <div className={styles.blockList}>
      {blocks.map((block, index) => (
        <article key={index} className={styles.blockCard}>
          <div className={styles.blockHead}>
            <span className={styles.blockType}>{BLOCK_LABELS[block.type]}</span>
            <div className={styles.blockActions}>
              {onImproveBlock &&
              (block.type === 'paragraph' || block.type === 'heading' || block.type === 'cta') ? (
                <Button
                  type="button"
                  variant="ghost"
                  disabled={disabled || improvingBlockIndex === index}
                  onClick={() => onImproveBlock(index)}
                >
                  {improvingBlockIndex === index ? 'AI…' : 'Verbeter'}
                </Button>
              ) : null}
              <Button
                type="button"
                variant="ghost"
                disabled={disabled || index === 0}
                onClick={() => onChange(moveBlock(blocks, index, -1))}
              >
                ↑
              </Button>
              <Button
                type="button"
                variant="ghost"
                disabled={disabled || index >= blocks.length - 1}
                onClick={() => onChange(moveBlock(blocks, index, 1))}
              >
                ↓
              </Button>
              <Button
                type="button"
                variant="ghost"
                disabled={disabled || blocks.length <= 1}
                onClick={() => onChange(removeBlockAt(blocks, index))}
              >
                Verwijder
              </Button>
            </div>
          </div>

          {block.type === 'heading' || block.type === 'paragraph' ? (
            <Field label={block.type === 'heading' ? 'Koptekst' : 'Tekst'}>
              <TextArea
                value={block.text}
                disabled={disabled}
                rows={block.type === 'heading' ? 2 : 4}
                placeholder={
                  block.type === 'heading'
                    ? 'Kop met {{company}}'
                    : 'Hoi {{voornaam}},\n\n…'
                }
                onFocus={() => onFocusField?.(index, 'text')}
                onChange={(e) =>
                  onChange(updateBlockAt(blocks, index, { ...block, text: e.target.value }))
                }
              />
            </Field>
          ) : null}

          {block.type === 'cta' ? (
            <>
              <Field label="Knoptekst">
                <TextInput
                  value={block.label}
                  disabled={disabled}
                  onFocus={() => onFocusField?.(index, 'label')}
                  onChange={(e) =>
                    onChange(updateBlockAt(blocks, index, { ...block, label: e.target.value }))
                  }
                />
              </Field>
              <Field label="URL">
                <TextInput
                  value={block.url_template}
                  disabled={disabled}
                  onFocus={() => onFocusField?.(index, 'url_template')}
                  onChange={(e) =>
                    onChange(
                      updateBlockAt(blocks, index, { ...block, url_template: e.target.value }),
                    )
                  }
                />
              </Field>
            </>
          ) : null}

          {block.type === 'divider' ? (
            <p className={styles.muted}>Horizontale lijn in de e-mail.</p>
          ) : null}
        </article>
      ))}

      <div className={styles.addRow}>
        <Button
          type="button"
          variant="secondary"
          disabled={disabled}
          onClick={() =>
            onChange(insertBlockAfter(blocks, blocks.length - 1, { type: 'paragraph', text: '' }))
          }
        >
          + Alinea
        </Button>
        <Button
          type="button"
          variant="secondary"
          disabled={disabled}
          onClick={() =>
            onChange(insertBlockAfter(blocks, blocks.length - 1, { type: 'heading', text: '' }))
          }
        >
          + Kop
        </Button>
        <Button
          type="button"
          variant="secondary"
          disabled={disabled}
          onClick={() => onChange(insertBlockAfter(blocks, blocks.length - 1, { type: 'divider' }))}
        >
          + Lijn
        </Button>
        <Button
          type="button"
          variant="secondary"
          disabled={disabled}
          onClick={() =>
            onChange(
              insertBlockAfter(blocks, blocks.length - 1, {
                type: 'cta',
                label: 'Meer info',
                url_template: 'https://',
              }),
            )
          }
        >
          + CTA
        </Button>
      </div>
    </div>
  );
}
